import { seedKeyGen } from "@filosign/crypto-utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFilosignContext } from "../../context/useFilosignContext";
import { filosignKeys } from "../../lib/query-keys";
import type { KeyRegistrySnapshot } from "./key-registry-snapshot";
import { fetchKeyRegistrySnapshot } from "./key-registry-snapshot";
import { seedFromRecoveryPhrase } from "./recovery-phrase";
import { setSessionSeed } from "./session-seed";

export function useRecoverWithPhrase() {
	const { wallet, rpc, wasm } = useFilosignContext();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (params: { phrase: string }) => {
			if (!wallet || !rpc || !wasm.dilithium) {
				throw new Error("unreachable");
			}
			const address = wallet.account.address as `0x${string}`;
			const seed = await seedFromRecoveryPhrase(params.phrase);
			const keygenData = await seedKeyGen(new Uint8Array(Array.from(seed)), {
				dl: wasm.dilithium,
			});

			const cached = queryClient.getQueryData<KeyRegistrySnapshot>(
				filosignKeys.keyRegistrySnapshot(address),
			);
			let commitmentKem = cached?.storedKeygenData?.commitmentKem;
			let commitmentSig = cached?.storedKeygenData?.commitmentSig;
			if (!commitmentKem || !commitmentSig) {
				const snapshot = await fetchKeyRegistrySnapshot(rpc, address);
				commitmentKem = snapshot.storedKeygenData?.commitmentKem;
				commitmentSig = snapshot.storedKeygenData?.commitmentSig;
			}

			if (
				!commitmentKem ||
				!commitmentSig ||
				commitmentKem !== keygenData.commitmentKem ||
				commitmentSig !== keygenData.commitmentSig
			) {
				throw new Error("Unable to unlock");
			}

			setSessionSeed(wallet.account.address, seed);
			await queryClient.refetchQueries({
				queryKey: filosignKeys.isLoggedIn(address),
			});
			return true;
		},
	});
}
